/**
 * Copyright ©2023 Drivepoint
 */

import React, {createRef, useEffect, useRef, useState} from "react";
import {Group, Lock, Menu, OpenInNewOutlined} from "@mui/icons-material";
import {Badge, ListItemButton, ListItemIcon, ListItemText, Tooltip} from "@mui/material";
import DPMenu, {StandardMenuInterface, StandardMenuItem} from "@components/DPMenu/DPMenu";
import usePage from "@hooks/usePage";
import useStateChange from "@hooks/useStateChange";
import Telemetry from "@services/telemetry/Telemetry";
import DrivepointUser from "@services/user/DrivepointUser";
import EventUtilities from "@utilities/EventUtilities";
import "./NavigationPageItem.css";

export type NavigationPageItemProps = {
  item: any;
  open?: boolean;
  currentPage?: any;
  onPageClick: (event: any, page: any) => void;
  onMenuItemClick: (page: any, item: StandardMenuItem, data?: any) => void;
};

export default function NavigationPageItem(props: NavigationPageItemProps): any {

  const menu = createRef<StandardMenuInterface>();
  const ref = useRef<HTMLElement>();

  const user = useStateChange<DrivepointUser>("user");
  const page = usePage();
  const navBadgeCount = useStateChange<number>(props.item.navBadgeStateKey ?? "__unused__");

  const [truncated, setTruncated] = useState<boolean>(false);

  useEffect(() => {
    if (!ref.current) { return; }
    if (!props.open) {
      setTruncated(false);
      return;
    }
    setTimeout(() => {
      const element: HTMLElement = ref.current?.getElementsByClassName("MuiListItemText-primary")?.[0] as HTMLElement;
      if (!element) { return; }
      setTruncated(element.offsetWidth < element.scrollWidth);
    }, 500);
  }, [ref.current, props, open]);

  function addSymbolToName(name: string) {
    if (name.toLowerCase() === "smartmodel") {
      return <>{name}<span style={{fontSize: "22px", lineHeight: 0}}>&#8482;</span></>;
    }
    return name;
  }

  function renderVisibilityIcon(): any {
    switch (props.item.visibility) {
      case "company": return <ListItemIcon className="navigation-section-page-visibility-icon"><Group /></ListItemIcon>;
      case "user": return <ListItemIcon className="navigation-section-page-visibility-icon"><Lock /></ListItemIcon>;
    }
  }

  function renderNavigationIcon(): any {
    if ((user || props.item.public) && props.item.external) {
      return <ListItemIcon className="navigation-section-page-icon"><OpenInNewOutlined /></ListItemIcon>;
    }
  }

  function onMenuClick(event: any): void {
    EventUtilities.stop(event);
    menu.current?.open(event.target);
  }

  function onMenuItemClick(item: StandardMenuItem, data?: any): void {
    props.onMenuItemClick(props.item, {...item, token: props.item.menu.token} as any, data);
  }

  function renderMenuIcon(): any {
    if (!props.item.menu?.items?.length) { return; }
    return <>
      <ListItemButton disableRipple className="navigation-section-page-icon" onClick={onMenuClick}><Menu /></ListItemButton>
      <DPMenu
        items={props.item.menu.items.map((item: any) => ({...item, sxOverride: {fontSize: "12px"}}))}
        onClick={onMenuItemClick}
        ref={menu}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        transformOrigin={{vertical: "top", horizontal: "left"}}
      />
    </>;
  }

  function renderIcon(): any {
    const badgeCount = props.item.navBadgeStateKey && navBadgeCount ? navBadgeCount : 0;
    const icon = <ListItemIcon className="navigation-section-page-icon">
      {props.item.icon && <Badge badgeContent={badgeCount || null} color="warning" max={99}>
        <props.item.icon />
      </Badge>}
    </ListItemIcon>;
    if (props.open === false) {
      return <Tooltip title={props.item.name} arrow placement="right">
        <div>{icon}</div>
      </Tooltip>;
    }
    return icon;
  }

  function renderName(): any {
    if (truncated) {
      return <Tooltip title={addSymbolToName(props.item.name)} arrow placement="right">
        <ListItemText className="navigation-section-page-text" primary={addSymbolToName(props.item.name)} ref={ref} />
      </Tooltip>;
    } else {
      return <ListItemText className="navigation-section-page-text" primary={addSymbolToName(props.item.name)} ref={ref} />;
    }
  }

  const classes = ["navigation-section-page"];
  if (props.item.id === page.current?.id) { classes.push("highlight"); }

  return <ListItemButton
    className={`${classes.join(" ")}`}
    onClick={(event: any) => {
      if (props.item.name.toLowerCase() === "reports") { Telemetry.track("clickreportmenu"); }
      props.onPageClick(event, props.item);
    }}
  >
    {renderIcon()}
    {renderVisibilityIcon()}
    {renderName()}
    <div className="navigation-section-page-icons">
      {renderNavigationIcon()}
      {renderMenuIcon()}
    </div>
  </ListItemButton>;

}
